import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { leadsAPI, messagesAPI, landingPagesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Users, Mail, Phone, Calendar, MessageSquare, 
  Send, Trash2, Loader2, FileText, Filter, Search, Plus, Upload, CheckCircle2, AlertCircle, Youtube, Link2, CheckSquare, Square, Eye, MousePointerClick
} from 'lucide-react';
import { toast } from 'sonner';

const Leads = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Message dialog
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [messageForm, setMessageForm] = useState({ subject: '', content: '', youtube_url: '', cta_link: '', cta_text: '' });
  const [sending, setSending] = useState(false);
  
  // Lead detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [leadDetail, setLeadDetail] = useState(null);
  const [leadMessages, setLeadMessages] = useState([]);

  // Manual add dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', phone: '', landing_page_id: '' });
  const [addingSingle, setAddingSingle] = useState(false);

  // CSV import dialog
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPageId, setCsvPageId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const isPaidPlan = user?.plan === 'pro' || user?.plan === 'power';

  useEffect(() => {
    fetchData();
  }, [selectedPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, pagesRes, statsRes] = await Promise.all([
        leadsAPI.getAll(selectedPage !== 'all' ? selectedPage : null),
        landingPagesAPI.getAll(),
        leadsAPI.getStats()
      ]);
      setLeads(leadsRes.data.leads);
      setPages(pagesRes.data.landing_pages);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const openMessageDialog = (lead) => {
    setSelectedLead(lead);
    setMessageForm({ 
      subject: `Follow up from ${user?.name || 'TrafficFlow AI'}`,
      content: `Hi ${lead.name},\n\nThank you for your interest!\n\n`
    });
    setMessageDialogOpen(true);
  };

  const openDetailDialog = async (lead) => {
    setSelectedLead(lead);
    setDetailDialogOpen(true);
    
    try {
      const response = await leadsAPI.getOne(lead.id);
      setLeadDetail(response.data.lead);
      setLeadMessages(response.data.messages);
    } catch (error) {
      toast.error('Failed to load lead details');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!isPaidPlan) {
      toast.error('Messaging requires Pro or Power plan');
      return;
    }
    
    setSending(true);
    try {
      await messagesAPI.send({
        lead_id: selectedLead.id,
        subject: messageForm.subject,
        content: messageForm.content,
        youtube_url: messageForm.youtube_url || null,
        cta_link: messageForm.cta_link || null,
        cta_text: messageForm.cta_text || null,
      });
      toast.success('Message sent successfully!');
      setMessageDialogOpen(false);
      setMessageForm({ subject: '', content: '', youtube_url: '', cta_link: '', cta_text: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    
    try {
      await leadsAPI.delete(id);
      toast.success('Lead deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  // Manual add
  const handleAddLead = async (e) => {
    e.preventDefault();
    setAddingSingle(true);
    try {
      await leadsAPI.addManual(addForm);
      toast.success('Lead added!');
      setAddDialogOpen(false);
      setAddForm({ name: '', email: '', phone: '', landing_page_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lead');
    } finally {
      setAddingSingle(false);
    }
  };

  // CSV import
  const handleCsvImport = async (e) => {
    e.preventDefault();
    if (!csvFile || !csvPageId) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res = await leadsAPI.importCsv(csvFile, csvPageId);
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Bulk selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredLeads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredLeads.map(l => l.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} lead${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await leadsAPI.bulkDelete([...selectedIds]);
      toast.success(res.data.message);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const getPageName = (pageId) => {
    const page = pages.find(p => p.id === pageId);
    return page?.title || 'Unknown';
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      lead.name.toLowerCase().includes(search) ||
      lead.email.toLowerCase().includes(search) ||
      (lead.phone && lead.phone.includes(search))
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-neon-mint" />
            Leads
          </h1>
          <p className="text-gray-400">Manage your captured leads</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.plan === 'power' && (
            <Button
              onClick={() => { setImportResult(null); setCsvFile(null); setCsvPageId(''); setCsvDialogOpen(true); }}
              variant="outline"
              className="border-white/10 text-gray-300 hover:text-white"
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
          )}
          {user?.plan !== 'power' && user?.plan !== 'free' ? null : null}
          {(user?.plan === 'pro' || user?.plan === 'power') ? (
            <Button
              onClick={() => { setAddForm({ name: '', email: '', phone: '', landing_page_id: '' }); setAddDialogOpen(true); }}
              className="bg-neon-mint text-black hover:bg-neon-mint/80"
              data-testid="add-lead-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          ) : (
            <Button
              onClick={() => toast.info('Upgrade to Pro to add leads manually')}
              variant="outline"
              className="border-white/10 text-gray-500"
              data-testid="add-lead-btn-locked"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-neon-mint" />
              <span className="text-gray-400">Total Leads</span>
            </div>
            <p className="font-mono font-bold text-3xl text-white">{stats?.total_leads || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-5 h-5 text-neon-amber" />
              <span className="text-gray-400">This Week</span>
            </div>
            <p className="font-mono font-bold text-3xl text-white">{stats?.recent_leads || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-neon-violet" />
              <span className="text-gray-400">Landing Pages</span>
            </div>
            <p className="font-mono font-bold text-3xl text-white">{pages.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare className="w-5 h-5 text-neon-mint" />
              <span className="text-gray-400">Messages Sent</span>
            </div>
            <p className="font-mono font-bold text-3xl text-white">
              {leads.filter(l => l.last_contacted).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search leads..."
            className="pl-10 bg-void border-white/10"
          />
        </div>
        <Select value={selectedPage} onValueChange={setSelectedPage}>
          <SelectTrigger className="w-64 bg-void border-white/10">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by page" />
          </SelectTrigger>
          <SelectContent className="bg-void-paper border-white/10">
            <SelectItem value="all">All Landing Pages</SelectItem>
            {pages.map(page => (
              <SelectItem key={page.id} value={page.id}>{page.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-white font-heading font-semibold text-xl mb-2">No leads yet</h3>
            <p className="text-gray-400">
              {pages.length === 0 
                ? 'Create a landing page to start capturing leads.'
                : 'Leads will appear here when visitors sign up on your landing pages.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-void-paper border-white/10">
          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border-b border-red-500/20">
              <span className="text-white text-sm font-medium">{selectedIds.size} selected</span>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="bulk-delete-btn"
              >
                {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Delete Selected
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedIds(new Set())}
                className="text-gray-400 hover:text-white"
              >
                Cancel
              </Button>
            </div>
          )}
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-500 text-sm border-b border-white/10">
                    <th className="p-4 w-10">
                      <button onClick={toggleSelectAll} className="text-gray-500 hover:text-white" data-testid="select-all-checkbox">
                        {selectedIds.size === filteredLeads.length && filteredLeads.length > 0
                          ? <CheckSquare className="w-4 h-4 text-neon-mint" />
                          : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                    <th className="p-4 font-medium">Lead</th>
                    <th className="p-4 font-medium">Source</th>
                    <th className="p-4 font-medium">Date</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium">Engagement</th>
                    <th className="p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <tr 
                      key={lead.id} 
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                      onClick={() => openDetailDialog(lead)}
                    >
                      <td className="p-4 w-10" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => toggleSelect(lead.id)} className="text-gray-500 hover:text-white" data-testid={`select-lead-${lead.id}`}>
                          {selectedIds.has(lead.id)
                            ? <CheckSquare className="w-4 h-4 text-neon-mint" />
                            : <Square className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{lead.name}</p>
                          <div className="flex items-center gap-3 text-gray-400 text-sm">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {lead.email}
                            </span>
                            {lead.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {lead.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 bg-white/5 rounded text-gray-400 text-sm">
                          {getPageName(lead.landing_page_id)}
                        </span>
                      </td>
                      <td className="p-4 text-gray-400 text-sm">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        {lead.message_count > 0 ? (
                          <span className="px-2 py-1 bg-neon-mint/10 text-neon-mint text-xs rounded flex items-center gap-1 w-fit">
                            <Mail className="w-3 h-3" />
                            {lead.message_count} sent
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-neon-amber/10 text-neon-amber text-xs rounded">
                            New
                          </span>
                        )}
                      </td>
                      <td className="p-4" data-testid={`lead-engagement-${lead.id}`}>
                        {lead.message_count > 0 ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {lead.email_opened ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded" title={lead.email_opened_at ? `Opened ${new Date(lead.email_opened_at).toLocaleString()}` : 'Opened'}>
                                <Eye className="w-3 h-3" />
                                Opened
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-white/5 text-gray-500 text-xs rounded">
                                <Eye className="w-3 h-3" />
                                Not opened
                              </span>
                            )}
                            {lead.email_clicks > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-neon-violet/10 text-neon-violet text-xs rounded">
                                <MousePointerClick className="w-3 h-3" />
                                {lead.email_clicks} click{lead.email_clicks > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openMessageDialog(lead)}
                            className="text-gray-400 hover:text-neon-mint"
                            title="Send Message"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteLead(lead.id)}
                            className="text-gray-400 hover:text-red-400"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Message Dialog */}
      <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
        <DialogContent className="bg-void-paper border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-neon-mint" />
              Send Message to {selectedLead?.name}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={sendMessage} className="space-y-4">
            <div className="bg-void rounded-lg p-4">
              <p className="text-gray-400 text-sm">Sending to:</p>
              <p className="text-white">{selectedLead?.email}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={messageForm.subject}
                onChange={(e) => setMessageForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Subject line"
                required
                className="bg-zinc-900 border-white/10 text-white placeholder:text-gray-500"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={messageForm.content}
                onChange={(e) => setMessageForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Your message..."
                required
                rows={5}
                className="bg-zinc-900 border-white/10 text-white placeholder:text-gray-500 resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Youtube className="w-4 h-4 text-red-500" />
                YouTube Video
                <span className="text-gray-500 text-xs font-normal">(optional)</span>
              </Label>
              <Input
                type="url"
                value={messageForm.youtube_url}
                onChange={(e) => setMessageForm(prev => ({ ...prev, youtube_url: e.target.value }))}
                placeholder="https://youtube.com/watch?v=..."
                className="bg-zinc-900 border-white/10 text-white placeholder:text-gray-500"
                data-testid="message-youtube-url"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-neon-mint" />
                Link / CTA Button
                <span className="text-gray-500 text-xs font-normal">(optional)</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={messageForm.cta_link}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, cta_link: e.target.value }))}
                  placeholder="https://yourwebsite.com"
                  className="bg-zinc-900 border-white/10 text-white placeholder:text-gray-500 flex-1"
                  data-testid="message-cta-link"
                />
                <Input
                  value={messageForm.cta_text}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, cta_text: e.target.value }))}
                  placeholder="Button text"
                  className="bg-zinc-900 border-white/10 text-white placeholder:text-gray-500 w-36"
                  data-testid="message-cta-text"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setMessageDialogOpen(false)}
                className="text-gray-400"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sending || !isPaidPlan}
                className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-void-paper border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Lead Details</DialogTitle>
          </DialogHeader>
          
          {leadDetail && (
            <div className="space-y-6">
              {/* Lead Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-void rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Name</p>
                  <p className="text-white font-medium">{leadDetail.name}</p>
                </div>
                <div className="bg-void rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Email</p>
                  <p className="text-white font-medium">{leadDetail.email}</p>
                </div>
                {leadDetail.phone && (
                  <div className="bg-void rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">Phone</p>
                    <p className="text-white font-medium">{leadDetail.phone}</p>
                  </div>
                )}
                <div className="bg-void rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Captured</p>
                  <p className="text-white font-medium">
                    {new Date(leadDetail.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              
              {/* Message History */}
              <div>
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-neon-violet" />
                  Message History
                </h4>
                {leadMessages.length === 0 ? (
                  <p className="text-gray-500 text-sm">No messages sent yet.</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {leadMessages.map((msg) => (
                      <div key={msg.id} className="bg-void rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white font-medium">{msg.subject || 'Message'}</p>
                          <span className="text-gray-500 text-xs">
                            {new Date(msg.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm whitespace-pre-wrap line-clamp-3">{msg.content}</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          {msg.email_sent && (
                            <span className="inline-flex items-center text-xs text-neon-mint gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Delivered
                            </span>
                          )}
                          {msg.tracking?.opened && (
                            <span className="inline-flex items-center text-xs text-blue-400 gap-1">
                              <Eye className="w-3 h-3" /> Opened
                              {msg.tracking.opened_at && (
                                <span className="text-gray-500 ml-1">
                                  {new Date(msg.tracking.opened_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </span>
                          )}
                          {msg.tracking?.clicks?.length > 0 && (
                            <span className="inline-flex items-center text-xs text-neon-violet gap-1">
                              <MousePointerClick className="w-3 h-3" /> {msg.tracking.clicks.length} click{msg.tracking.clicks.length > 1 ? 's' : ''}
                            </span>
                          )}
                          {msg.email_sent && msg.tracking && !msg.tracking.opened && (
                            <span className="inline-flex items-center text-xs text-gray-500 gap-1">
                              Not opened yet
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button
                  onClick={() => {
                    setDetailDialogOpen(false);
                    openMessageDialog(leadDetail);
                  }}
                  className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-void-paper border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Plus className="w-5 h-5 text-neon-mint" />
              Add Lead Manually
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLead} className="space-y-4">
            <div className="space-y-2">
              <Label>Capture Page <span className="text-red-400">*</span></Label>
              <Select value={addForm.landing_page_id} onValueChange={(val) => setAddForm(prev => ({ ...prev, landing_page_id: val }))}>
                <SelectTrigger className="bg-void border-white/10" data-testid="add-lead-page-select">
                  <SelectValue placeholder="Select a capture page" />
                </SelectTrigger>
                <SelectContent className="bg-void-paper border-white/10">
                  {pages.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Email <span className="text-red-400">*</span></Label>
              <Input
                type="email"
                value={addForm.email}
                onChange={(e) => setAddForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="lead@example.com"
                required
                className="bg-void border-white/10"
                data-testid="add-lead-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe"
                className="bg-void border-white/10"
                data-testid="add-lead-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={addForm.phone}
                onChange={(e) => setAddForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 234 567 8900"
                className="bg-void border-white/10"
                data-testid="add-lead-phone"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAddDialogOpen(false)} className="text-gray-400">Cancel</Button>
              <Button type="submit" disabled={addingSingle || !addForm.email || !addForm.landing_page_id} className="bg-neon-mint text-black hover:bg-neon-mint/80" data-testid="add-lead-submit">
                {addingSingle ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Lead
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvDialogOpen} onOpenChange={(open) => { if (!open) { setCsvDialogOpen(false); setImportResult(null); } }}>
        <DialogContent className="bg-void-paper border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Upload className="w-5 h-5 text-neon-mint" />
              Import Leads from CSV
            </DialogTitle>
          </DialogHeader>

          {importResult ? (
            <div className="space-y-4">
              <div className="bg-void rounded-lg p-6 text-center">
                <CheckCircle2 className="w-12 h-12 text-neon-mint mx-auto mb-3" />
                <h3 className="text-white font-semibold text-lg mb-4">Import Complete</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-neon-mint">{importResult.imported}</p>
                    <p className="text-gray-400 text-sm">Imported</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-neon-amber">{importResult.skipped_duplicate}</p>
                    <p className="text-gray-400 text-sm">Duplicates</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-400">{importResult.skipped_invalid}</p>
                    <p className="text-gray-400 text-sm">Invalid</p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setCsvDialogOpen(false); setImportResult(null); }} className="bg-neon-mint text-black hover:bg-neon-mint/80">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleCsvImport} className="space-y-4">
              <div className="space-y-2">
                <Label>Capture Page <span className="text-red-400">*</span></Label>
                <Select value={csvPageId} onValueChange={setCsvPageId}>
                  <SelectTrigger className="bg-void border-white/10" data-testid="csv-page-select">
                    <SelectValue placeholder="Select a capture page" />
                  </SelectTrigger>
                  <SelectContent className="bg-void-paper border-white/10">
                    {pages.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>CSV File <span className="text-red-400">*</span></Label>
                <div className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center hover:border-neon-mint/30 transition-colors">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0] || null)}
                    className="hidden"
                    id="csv-upload"
                    data-testid="csv-file-input"
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                    {csvFile ? (
                      <p className="text-neon-mint font-medium">{csvFile.name}</p>
                    ) : (
                      <p className="text-gray-400">Click to select a CSV file</p>
                    )}
                    <p className="text-gray-600 text-xs mt-1">Max 5,000 rows / 5 MB</p>
                  </label>
                </div>
              </div>
              <div className="bg-void rounded-lg p-4">
                <p className="text-gray-400 text-sm font-medium mb-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> CSV Format
                </p>
                <p className="text-gray-500 text-xs">Required column: <span className="text-white">email</span></p>
                <p className="text-gray-500 text-xs">Optional columns: <span className="text-white">name</span>, <span className="text-white">phone</span></p>
                <p className="text-gray-500 text-xs mt-1">Duplicates and invalid emails are automatically skipped.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setCsvDialogOpen(false)} className="text-gray-400">Cancel</Button>
                <Button type="submit" disabled={importing || !csvFile || !csvPageId} className="bg-neon-mint text-black hover:bg-neon-mint/80" data-testid="csv-import-submit">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  {importing ? 'Importing...' : 'Import Leads'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
